import React from 'react';
import { Check, ChevronDown, ChevronUp } from 'lucide-react';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { cn } from '@/lib/utils';

function initials(name?: string | null): string {
  const source = (name || '?').trim();
  const parts = source.split(/\s+/).filter(Boolean);
  if (parts.length >= 2) return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
  return source.slice(0, 2).toUpperCase();
}

interface AnswerVoteProps {
  score: number;
  myVote: number;
  disabled?: boolean;
  onVote: (direction: 1 | -1) => void;
}

export function byAnswerRank<T extends { score: number; created_at: string; is_accepted?: boolean }>(items: T[]): T[] {
  return [...items].sort((a, b) => {
    const accepted = Number(Boolean(b.is_accepted)) - Number(Boolean(a.is_accepted));
    if (accepted !== 0) return accepted;
    if (b.score !== a.score) return b.score - a.score;
    return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
  });
}

interface RightAnswerControlProps {
  accepted: boolean;
  canMark: boolean;
  disabled?: boolean;
  onToggle: () => void;
}

export function RightAnswerControl({ accepted, canMark, disabled = false, onToggle }: RightAnswerControlProps) {
  if (!accepted && !canMark) return null;
  if (!canMark) {
    return (
      <span className="inline-flex items-center gap-1 font-medium text-emerald-600">
        <Check className="h-3.5 w-3.5" />
        Right answer
      </span>
    );
  }
  return (
    <button
      type="button"
      aria-pressed={accepted}
      disabled={disabled}
      onClick={onToggle}
      className={cn(
        'inline-flex items-center gap-1 rounded-full px-2 py-0.5 font-medium',
        accepted ? 'bg-emerald-600/10 text-emerald-700' : 'text-muted-foreground hover:bg-muted hover:text-foreground',
      )}
    >
      <Check className="h-3.5 w-3.5" />
      {accepted ? 'Right answer' : 'Mark as right answer'}
    </button>
  );
}

export function AnswerVote({ score, myVote, disabled = false, onVote }: AnswerVoteProps) {
  return (
    <div className="flex flex-col items-center">
      <button
        type="button"
        aria-label="Upvote"
        aria-pressed={myVote === 1}
        disabled={disabled}
        className="rounded-md p-0.5 text-muted-foreground hover:bg-muted hover:text-foreground disabled:opacity-50"
        onClick={() => onVote(1)}
      >
        <ChevronUp className={cn('h-5 w-5', myVote === 1 && 'text-orange-500')} />
      </button>
      <span
        className={cn(
          'text-xs font-semibold tabular-nums',
          myVote === 1 && 'text-orange-500',
          myVote === -1 && 'text-blue-600',
        )}
      >
        {score}
      </span>
      <button
        type="button"
        aria-label="Downvote"
        aria-pressed={myVote === -1}
        disabled={disabled}
        className="rounded-md p-0.5 text-muted-foreground hover:bg-muted hover:text-foreground disabled:opacity-50"
        onClick={() => onVote(-1)}
      >
        <ChevronDown className={cn('h-5 w-5', myVote === -1 && 'text-blue-600')} />
      </button>
    </div>
  );
}

interface CommunityPostHeadingProps {
  authorName?: string | null;
  createdAt?: string | null;
  meta?: React.ReactNode;
}

export function CommunityPostHeading({ authorName, createdAt, meta }: CommunityPostHeadingProps) {
  const when = createdAt ? new Date(createdAt) : null;
  const label = when && !Number.isNaN(when.getTime()) ? when.toLocaleString() : '';

  return (
    <div className="flex items-center gap-2 text-xs text-muted-foreground">
      <Avatar className="h-7 w-7">
        <AvatarFallback className="bg-primary text-[10px] font-semibold text-primary-foreground">
          {initials(authorName)}
        </AvatarFallback>
      </Avatar>
      <span className="font-medium text-foreground">{authorName || 'Member'}</span>
      {label && <span>{label}</span>}
      {meta}
    </div>
  );
}
